import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { useStore } from 'src/contexts/StoreContext';
import TextLato from '../text/text-lato/TextLato';
import { ICON_NAMES } from 'src/editor-v3/components/icon/icon.helper';
import { Icon } from 'src/editor-v3/components/icon/Icon';
import { IconType } from '../lazy-icon/LazyIcon.types';
import { TextLink } from 'src/editor-v3/components/text-link/TextLink';
import { TEXT_LINK_SIZES, TEXT_LINK_TYPES } from 'src/editor-v3/components/text-link/TextLink.constants';
import { BUTTON_LAYOUTS } from 'src/editor-v3/components/buttons/button/Button.constants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { styles } from './DiscountScaleInfoDropup.styles';
import { discountScaleInfoDropupTestID } from 'src/helpers/testIDs.helper';
import { useState } from 'react';
import { DiscountScaleProgressMini } from '../discount-scale-progress/discount-scale-progress-mini/DiscountScaleProgressMini';
import { DiscountScaleInfoDropupProps } from './DiscountScaleInfoDropup.types';
import { makeBold } from 'src/helpers/text.helper';
import { useSharedValue } from 'react-native-reanimated';

export const DiscountScaleInfoDropup = observer(
  ({
    missingForNextDiscount,
    nextDiscount,
    discountValue,
    ...discountScaleInfoDropupProps
  }: DiscountScaleInfoDropupProps) => {
    const { i18n } = useStore();
    const [showDiscountsTable, setShowDiscountsTable] = useState(false);
    const anyMilestoneShowingCheckSV = useSharedValue<boolean>(false);

    const handleDiscoverDiscount = async () => {
      setShowDiscountsTable(prev => !prev);
    };

    const ctaText = showDiscountsTable ? i18n.t('actions:close') : i18n.t('discounts-table:discover-discount');
    return (
      <View testID={TestIDs.DISCOUNT_SCALE_INFO_DROPUP}>
        {showDiscountsTable && (
          <View style={styles.discountScaleProgressMiniContainer}>
            <DiscountScaleProgressMini
              {...discountScaleInfoDropupProps}
              anyMilestoneShowingCheckSV={anyMilestoneShowingCheckSV}
            />
          </View>
        )}
        <View style={styles.contentContainer}>
          <View style={styles.leftContent}>
            <Icon name={ICON_NAMES.Discount} type={IconType.PROMO} size={16} filled />

            {showDiscountsTable ? (
              nextDiscount && missingForNextDiscount && missingForNextDiscount > 0 ? (
                <TextLato style={styles.discountsTableTitle}>
                  {i18n.t('discounts-table:prints-to-reach-head')}
                  {makeBold(
                    i18n.t('discounts-table:prints-to-reach-middle', { count: missingForNextDiscount || undefined }),
                  )}
                  {i18n.t('discounts-table:prints-to-reach-tail', { discount: makeBold(`${nextDiscount}%`) })}
                </TextLato>
              ) : (
                <TextLato style={styles.title}>
                  {discountValue != '0'
                    ? i18n.t('discounts-table:prints-description', { discount: discountValue })
                    : i18n.t('discounts-table:prints-title')}
                </TextLato>
              )
            ) : (
              <TextLato style={styles.title}>{i18n.t('discounts-table:prints-title')}</TextLato>
            )}
          </View>
          <View>
            <TextLink
              size={TEXT_LINK_SIZES.DEFAULT}
              type={TEXT_LINK_TYPES.NO_ICON}
              onPress={handleDiscoverDiscount}
              testID={discountScaleInfoDropupTestID(TestIDs.DISCOVER_DISCOUNT)}
              focusId={discountScaleInfoDropupTestID(TestIDs.DISCOVER_DISCOUNT)}
              focusOrder={1}
              focusGroup={FocusGroups.BOTTOM_BAR}
              text={ctaText}
              layout={BUTTON_LAYOUTS.HUG}
              accessible={true}
              accessibilityLabel={ctaText}
              accessibilityRole="button"
            />
          </View>
        </View>
      </View>
    );
  },
);

DiscountScaleInfoDropup.displayName = 'DiscountScaleInfoDropup';
